// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { fetchActivePricesForRole } from "services/pricingService";
import { useStripe } from "@stripe/react-stripe-js";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Card,
} from "@mui/material";
import PricingControls from "./PricingControls";
import { LoadingButton } from "@mui/lab";
import UpgradeFeatures from "./UpgradeFeatures";
import { useAuth } from "hooks/useAuth";
import { getTrialLengthForRole } from "constants/trials";

const Pricing = ({ uid, role, subscription }) => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const stripe = useStripe();
  const {
    hasValidSubscription,
    canStartTrial,
    hasActiveTrial,
    trialExpired,
    userData,
  } = useAuth();
  const [trialLoading, setTrialLoading] = useState(null);
  const [prices, setPrices] = useState({});
  const [selectedInterval, setSelectedInterval] = useState("monthly");
  const [showPricing, setShowPricing] = useState(false);
  const trialLengthDays = getTrialLengthForRole(role);
  const isFacility = role === "facility";

  const getPlans = async () => {
    if (!role) return;
    try {
      const entries = await fetchActivePricesForRole(role);
      const priceList = {};

      for (const { priceId, price: priceData, product: productData } of entries) {
        const roundNumber = String(priceData.unit_amount).slice(-2) === "00";
        const formattedAmount = roundNumber
          ? priceData.unit_amount / 100
          : (priceData.unit_amount / 100).toFixed(2);

        const newPrice = {
          id: priceId,
          role: productData.role,
          price: `$${formattedAmount}`,
          interval: priceData.interval || "day",
          type: priceData.type || "recurring",
        };

        if (priceData.description) {
          const [tier, interval] = priceData.description.split("_");
          if (tier && interval) {
            // Standard format: "medium_monthly"
            if (!priceList[tier]) priceList[tier] = {};
            priceList[tier][interval] = newPrice;
          } else if (
            priceData.description === "monthly" ||
            priceData.description === "yearly"
          ) {
            // Facility format: just "monthly" or "yearly"
            if (!priceList["facility"]) priceList["facility"] = {};
            priceList["facility"][priceData.description] = newPrice;
          }
        } else {
          const key =
            priceData.type === "one_time" ? "day" : priceData.interval;
          priceList[key] = newPrice;
        }
      }

      setPrices(priceList);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  useEffect(() => {
    if (role) {
      getPlans();
    }
  }, [role]);

  const startTrial = async (planId = null) => {
    setTrialLoading(planId);
    try {
      const startTrialFunction = firebase
        .functions()
        .httpsCallable("startTrial");
      const result = await startTrialFunction();
      if (result.data.success) {
        console.log("Trial started successfully");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error starting trial:", error);
      alert(t("pricing.startFailed", { message: error.message }));
      setTrialLoading(null);
    }
  };

  return (
    <>
      {trialExpired && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>{t("pricing.trialExpiredTitle")}</AlertTitle>
          {isFacility
            ? t("pricing.trialEndedFacility")
            : t("pricing.trialEnded")}
        </Alert>
      )}

      {canStartTrial && !showPricing ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            my: 4,
          }}
        >
          <Typography variant="h4" sx={{ mb: 2 }}>
            {isFacility
              ? t("pricing.tryFacility")
              : t("pricing.tryIndividual")}
          </Typography>
          {isFacility && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 520, mx: "auto" }}
            >
              {t("pricing.facilityPitch")}
            </Typography>
          )}
          {!isFacility && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
            >
              {t("pricing.noCard", { count: trialLengthDays })}
            </Typography>
          )}
          <UpgradeFeatures
            role={role}
            dense
            sx={{
              textAlign: "left",
              maxWidth: 520,
              mx: "auto",
              mb: 3,
              color: "text.secondary",
              "& .MuiListItemIcon-root": { minWidth: 32 },
            }}
          />
          <LoadingButton
            loading={trialLoading === "startTrial"}
            disabled={trialLoading !== null}
            variant="contained"
            size="large"
            onClick={() => startTrial("startTrial")}
            sx={{ px: 5, py: 1.5, fontSize: "1.1rem", mb: 2 }}
          >
            {isFacility
              ? t("pricing.startFacility", { count: trialLengthDays })
              : t("pricing.startIndividual", { count: trialLengthDays })}
          </LoadingButton>
          <Box sx={{ mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowPricing(true)}
              sx={{
                color: "text.secondary",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {t("pricing.viewPlans")}
            </Link>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              maxWidth: 520,
              mx: "auto",
              textAlign: "left",
              mb: 3,
              color: "text.secondary",
            }}
          >
            <UpgradeFeatures
              role={role}
              dense
              sx={{
                "& .MuiListItemIcon-root": { minWidth: 32 },
                color: "text.secondary",
              }}
            />
          </Box>

          <Box id="pricing-plans">
            <PricingControls
              prices={prices}
              selectedInterval={selectedInterval}
              onIntervalChange={setSelectedInterval}
              canStartTrial={canStartTrial}
              startTrial={startTrial}
              trialLoading={trialLoading}
              subscription={subscription}
              role={role}
              trialExpired={trialExpired}
            />
          </Box>

          {canStartTrial && showPricing && (
            <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowPricing(false)}
                sx={{
                  color: "text.secondary",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t("pricing.backToTrial")}
              </Link>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default Pricing;
